import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Col,
  ListGroup,
  Button,
  Card,
  Form,
  Accordion,
} from 'react-bootstrap';
import {
  FaShoppingCart,
  FaStickyNote,
  FaTrash,
  FaBox,
  FaCashRegister,
  FaUsers,
} from 'react-icons/fa';
import Message from '../components/Message';
import { addToCart, removeFromCart, clearCartItems } from '../slices/cartSlice';

const StoreCartScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { cartItems } = useSelector((state) => state.cart);

  const nav = (path) => navigate(`/login?redirect=/${path}`);

  // ⭐ All pages visible, Mini-prefixed
  const categories = useMemo(
    () => ({
      'Mini Sales & Purchases': {
        color: '#007bff',
        icon: <FaShoppingCart className="me-2" />,
        items: {
          storeSalesform: { label: 'Mini StoreSalesForm' },
          dailystoreStocksform: { label: 'Mini DailyStoreStockForm' },
          storeSupplysform: { label: 'Mini StoreSupplyForm' },
          storeTransfersform: { label: 'Mini StoreTransferForm' },
          storeStocksform: { label: 'Mini StoreStock' },
          openingstoreStocksform: { label: 'Mini Op-StoreStock' },
        },
      },

      'Mini Billing': {
        color: '#6f42c1',
        icon: <FaStickyNote className="me-2" />,
        items: {
          insurancesform: { label: 'Mini InsuForm' },
        },
      },

      'Mini Income & Expense': {
        color: '#ffc107',
        icon: <FaStickyNote className="me-2" />,
        items: {
          storeExpenselist: { label: 'Mini Credits / Debits' },
        },
      },

      'Mini Products & Inventory': {
        color: '#28a745',
        icon: <FaBox className="me-2" />,
        items: {
          productlist: { label: 'Mini Itemlist' },
        },
      },

      'Mini Cash & Bank': {
        color: '#17a2b8',
        icon: <FaCashRegister className="me-2" />,
        items: {
          cashlist: { label: 'Mini Cash Collected' },
        },
      },

      'Mini People': {
        color: '#fd7e14',
        icon: <FaUsers className="me-2" />,
        items: {
          customeruserlist: { label: 'Mini Customerlist' },
          supplierlist: { label: 'Mini Supplierlist' },
        },
      },

      'Mini Misc': {
        color: '#6c757d',
        icon: <FaStickyNote className="me-2" />,
        items: {
          services: { label: 'Mini Services' },
          contact: { label: 'Mini Contact Us' },
        },
      },
    }),
    []
  );

  // ⭐ Search filters all visible items
  const [search, setSearch] = useState('');

  const filteredCategories = useMemo(() => {
    const result = {};

    Object.entries(categories).forEach(([catName, catData]) => {
      const matched = Object.entries(catData.items).filter(([path, item]) =>
        item.label.toLowerCase().includes(search.toLowerCase())
      );

      if (matched.length > 0) {
        result[catName] = {
          ...catData,
          items: Object.fromEntries(matched),
        };
      }
    });

    return result;
  }, [search, categories]);

  return (
    <Row>
      {/* LEFT PANEL */}
      <Col md={8}>
        <h1 className="mb-4">Mini Shopping Cart</h1>

        {cartItems.length === 0 ? (
          <Message>
            Your cart is empty <Link to="/house/HomeScreen">Go Back</Link>
          </Message>
        ) : (
          <ListGroup variant="flush">
            {cartItems.map((item) => (
              <ListGroup.Item key={item._id}>
                <Row className="align-items-center">
                  <Col md={2}>
                    {item.images?.length > 0 && (
                      <img
                        src={item.images[0]}
                        alt="Thumbnail"
                        style={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </Col>

                  <Col md={3}>
                    <Link to={`/product/${item._id}`}>{item.name}</Link>
                  </Col>

                  <Col md={2}>D{item.price}</Col>

                  <Col md={2}>
                    <Button
                      variant="light"
                      onClick={() =>
                        dispatch(addToCart({ ...item, qty: item.qty - 1 }))
                      }
                      disabled={item.qty === 1}
                    >
                      -
                    </Button>
                    <span className="mx-2">{item.qty}</span>
                    <Button
                      variant="light"
                      onClick={() =>
                        dispatch(addToCart({ ...item, qty: item.qty + 1 }))
                      }
                      disabled={item.qty === item.countInStoreStock}
                    >
                      +
                    </Button>
                  </Col>

                  <Col md={2}>D{(item.price * item.qty).toFixed(2)}</Col>

                  <Col md={1}>
                    <Button
                      variant="light"
                      onClick={() => dispatch(removeFromCart(item._id))}
                    >
                      <FaTrash />
                    </Button>
                  </Col>
                </Row>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Col>

      {/* RIGHT PANEL */}
      <Col md={4}>
        <Card>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>
                Mini Subtotal ({cartItems.reduce((acc, item) => acc + item.qty, 0)}) items
              </h2>
              D
              {cartItems
                .reduce((acc, item) => acc + item.qty * item.price, 0)
                .toFixed(2)}
            </ListGroup.Item>

            {/* SEARCH BAR */}
            <ListGroup.Item>
              <Form.Control
                type="text"
                placeholder="Search Mini actions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </ListGroup.Item>

            {/* COLLAPSIBLE SECTIONS */}
            <ListGroup.Item>
              <Accordion alwaysOpen>
                {Object.entries(filteredCategories).map(
                  ([catName, { color, icon, items }], idx) => (
                    <Accordion.Item eventKey={idx.toString()} key={catName}>
                      <Accordion.Header>
                        <span style={{ color }}>{icon}{catName}</span>
                      </Accordion.Header>

                      <Accordion.Body>
                        <Row>
                          {Object.entries(items).map(([path, item]) => (
                            <Col md={6} className="mb-2" key={path}>
                              <Button
                                className="w-100"
                                style={{
                                  backgroundColor: color,
                                  borderColor: color,
                                }}
                                disabled={cartItems.length === 0}
                                onClick={() => nav(path)}
                              >
                                {item.label}
                              </Button>
                            </Col>
                          ))}
                        </Row>
                      </Accordion.Body>
                    </Accordion.Item>
                  )
                )}
              </Accordion>
            </ListGroup.Item>

            {/* CLEAR CART */}
            <ListGroup.Item>
              <Button
                className="w-100"
                disabled={cartItems.length === 0}
                onClick={() => dispatch(clearCartItems())}
              >
                Clear Mini Cart
              </Button>
            </ListGroup.Item>
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default StoreCartScreen;
